import React, { useState, useEffect } from 'react';
import { Modal, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Dropdown from 'components/Elements/Dropdown';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import { CalendarIcon } from 'components/Elements/CustomSVGIcon';
import './FilterModal.scss';
import { FilterOptions, FilterModalValues } from '../../types';

interface FilterModalProps {
    visible: boolean;
    onClose: () => void;
    onApply: (values: FilterModalValues) => void;
    onClearAll: () => void;
    filterOptions: FilterOptions;
    initialValues: FilterModalValues;
}

const FilterModal: React.FC<FilterModalProps> = ({
    visible,
    onClose,
    onApply,
    onClearAll,
    filterOptions,
    initialValues,
}) => {
    const [localValues, setLocalValues] = useState<FilterModalValues>(initialValues);

    useEffect(() => {
        if (visible) {
            setLocalValues(initialValues);
        }
    }, [visible, initialValues]);

    const handleDropdownChange = (field: keyof FilterModalValues, values: DropdownOption[]) => {
        setLocalValues(prev => ({
            ...prev,
            [field]: values.map(v => v.value),
        }));
    };

    const handleDateChange = (field: keyof FilterModalValues, date: Dayjs | null) => {
        setLocalValues(prev => ({
            ...prev,
            [field]: date ? date.format('YYYY-MM-DD') : '',
        }));
    };

    const handleApply = () => {
        onApply(localValues);
        onClose();
    };

    const handleClearAll = () => {
        const clearedValues: FilterModalValues = {
            referenceNumber: [],
            site: [],
            requesterName: [],
            startDate: '',
            endDate: '',
            statusText: [],
            dateSubmittedStart: '',
            dateSubmittedEnd: '',
            approverName: [],
            requestType: [],
            requesterType: [],
        };
        setLocalValues(clearedValues);
        onClearAll();
    };

    const getSelectedValues = (field: string[], options: DropdownOption[]): DropdownOption[] => options.filter(opt => field.includes(opt.value));

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
            className="filter-modal"
            centered>
            <div className="filter-modal-container">
                <h2 className="heading">Filters</h2>
                <div className="filter-grid">
                    {/* Row 1 */}
                    <div className="filter-field">
                        <h3>Reference Number</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.referenceNumbers}
                                onSelect={(values) => handleDropdownChange('referenceNumber', values)}
                                values={getSelectedValues(localValues.referenceNumber, filterOptions.referenceNumbers)}
                                multi
                                placeholder="Select reference numbers..." />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Site</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.sites}
                                onSelect={(values) => handleDropdownChange('site', values)}
                                values={getSelectedValues(localValues.site, filterOptions.sites)}
                                multi
                                placeholder="Select sites..." />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Requester Name</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.requesterNames}
                                onSelect={(values) => handleDropdownChange('requesterName', values)}
                                values={getSelectedValues(localValues.requesterName, filterOptions.requesterNames)}
                                multi
                                placeholder="Select requesters..." />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="filter-field">
                        <h3>Requested Period</h3>
                        <div className="date-range">
                            <DatePicker
                                value={localValues.startDate ? dayjs(localValues.startDate) : null}
                                onChange={(date) => handleDateChange('startDate', date)}
                                placeholder="Start Date"
                                format="DD/MM/YYYY"
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                            <DatePicker
                                value={localValues.endDate ? dayjs(localValues.endDate) : null}
                                onChange={(date) => handleDateChange('endDate', date)}
                                placeholder="End Date"
                                format="DD/MM/YYYY"
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Request Status</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.statuses}
                                onSelect={(values) => handleDropdownChange('statusText', values)}
                                values={getSelectedValues(localValues.statusText, filterOptions.statuses)}
                                multi
                                placeholder="Select statuses..." />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Date Submitted</h3>
                        <div className="date-range">
                            <DatePicker
                                value={localValues.dateSubmittedStart ? dayjs(localValues.dateSubmittedStart) : null}
                                onChange={(date) => handleDateChange('dateSubmittedStart', date)}
                                placeholder="Start Date"
                                format="DD/MM/YYYY"
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                            <DatePicker
                                value={localValues.dateSubmittedEnd ? dayjs(localValues.dateSubmittedEnd) : null}
                                onChange={(date) => handleDateChange('dateSubmittedEnd', date)}
                                placeholder="End Date"
                                format="DD/MM/YYYY"
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                        </div>
                    </div>

                    {/* Row 3 */}
                    <div className="filter-field">
                        <h3>Approver Name</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.approverNames}
                                onSelect={(values) => handleDropdownChange('approverName', values)}
                                values={getSelectedValues(localValues.approverName, filterOptions.approverNames)}
                                multi
                                placeholder="Select approvers..." />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Request Type</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.requestTypes}
                                onSelect={(values) => handleDropdownChange('requestType', values)}
                                values={getSelectedValues(localValues.requestType, filterOptions.requestTypes)}
                                multi
                                placeholder="Select request types..." />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Booked By</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.requesterTypes}
                                onSelect={(values) => handleDropdownChange('requesterType', values)}
                                values={getSelectedValues(localValues.requesterType, filterOptions.requesterTypes)}
                                multi
                                placeholder="Select who booked..." />
                        </div>
                    </div>
                </div>

                <div className="button-wrapper">
                    <button type="button" className="clear-button" onClick={handleClearAll}>Clear All</button>
                    <button type="button" className="apply-button" onClick={handleApply}>Apply</button>
                    <button type="button" className="cancel-button" onClick={onClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
};

export default FilterModal;
